import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getSupplierById, createSupplier, updateSupplier } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import FloatingInput from "../components/UI/FloatingInput";
import ToggleSwitch from "../components/UI/ToggleSwitch";
import { notify } from "../utils/notify";
import PageHeader from "../components/UI/PageHeader";
import EntityTranslationsButton from "../components/UI/EntityTranslationsButton";
import { useTranslation } from "../context/I18nContext";

export default function AddEditSupplier() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const { t } = useTranslation("suppliers");
  const { t: tCommon } = useTranslation("common");

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    tax_number: "",
    is_active: true,
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (id) {
      getSupplierById(token, id).then((sup) => {
        setForm({
          name: sup.name || "",
          phone: sup.phone || "",
          email: sup.email || "",
          address: sup.address || "",
          tax_number: sup.tax_number || "",
          is_active: sup.is_active !== undefined ? sup.is_active : true,
        });
      }).catch(() => notify.error(t("notify_load_failed", "Failed to load supplier")));
    }
  }, [id, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const setField = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSubmit = async () => {
    setErrors({});
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = t("validation_name_required", "Supplier name is required");

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      notify.error(tCommon("validation_fill_required_fields", "Please fill in the required fields correctly"));
      return;
    }

    setLoading(true);
    const payload = {
      name: form.name.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      address: form.address.trim(),
      tax_number: form.tax_number.trim(),
      is_active: form.is_active,
    };

    try {
      if (id) {
        await updateSupplier(token, id, payload);
        notify.success(t("notify_updated", "Supplier updated successfully"));
      } else {
        await createSupplier(token, payload);
        notify.success(t("notify_created", "Supplier created successfully"));
      }
      navigate("/suppliers");
    } catch (err) {
      notify.error(err.data?.error || err.message || tCommon("error_generic", "Something went wrong"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto">
      <PageHeader className="flex items-center gap-4 mb-8 border-b border-gray-100 pb-6">
        <button onClick={() => navigate("/suppliers")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
          <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
        </button>
        <div>
          <h2 className="text-2xl font-black text-gray-800 tracking-tight">{id ? t("edit_title", "Edit Supplier") : t("add_title", "Add New Supplier")}</h2>
          <p className="text-gray-400 text-sm italic font-medium">{t("form_subtitle", "Contact details used on every Purchase Order for this supplier")}</p>
        </div>
      </PageHeader>

      <div className="bg-white p-6 md:p-8 rounded-[2.5rem] border border-gray-100 shadow-sm">
        <div className="space-y-6">
          <div className="flex items-end gap-3">
            <div className="flex-grow">
              <FloatingInput label={t("field_name_label", "Supplier Name")} value={form.name} onChange={setField("name")} error={errors.name} icon={ICONS.Suppliers} />
            </div>
            <EntityTranslationsButton entityType="supplier" entityId={id} defaultName={form.name} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FloatingInput label={t("field_phone_label", "Phone (optional)")} value={form.phone} onChange={setField("phone")} />
            <FloatingInput label={t("field_email_label", "Email (optional)")} value={form.email} onChange={setField("email")} />
          </div>

          <FloatingInput label={t("field_address_label", "Address (optional)")} value={form.address} onChange={setField("address")} />
          <FloatingInput label={t("field_tax_number_label", "Tax Number (optional)")} value={form.tax_number} onChange={setField("tax_number")} />

          <ToggleSwitch
            label={tCommon("status_active", "Active")}
            checked={form.is_active}
            onChange={(val) => setForm((prev) => ({ ...prev, is_active: val }))}
          />

          <div className="flex gap-4 pt-6 border-t border-gray-50">
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={loading}
              className="flex-grow shadow-lg shadow-blue-500/10"
              icon={id ? ICONS.Save : ICONS.Plus}
            >
              {id ? t("btn_update_supplier", "Update Supplier") : t("btn_create_supplier", "Create Supplier")}
            </Button>
            <Button variant="secondary" onClick={() => navigate("/suppliers")}>{tCommon("btn_cancel", "Cancel")}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
